import { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Plus,
  Calendar as CalendarIcon,
  Sparkles,
  Scissors,
  Palette,
  Utensils,
  PartyPopper,
  Heart,
  Cake,
  Star,
  GraduationCap,
  ShoppingBag,
  TrendingUp,
  Users,
  Building,
  Presentation,
  Wand2,
  GripVertical,
  Image as ImageIcon,
  Loader2,
  Check,
  Camera,
  Share
} from "lucide-react";
import { useExperienceCategories, ICON_MAP } from "@/hooks/useExperienceCategories";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  makeup: { bg: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500", border: "border-purple-200", light: "bg-purple-50" },
  hair: { bg: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500", border: "border-blue-200", light: "bg-blue-50" },
  nails: { bg: "bg-pink-100", text: "text-pink-700", dot: "bg-pink-500", border: "border-pink-200", light: "bg-pink-50" },
  nail: { bg: "bg-pink-100", text: "text-pink-700", dot: "bg-pink-500", border: "border-pink-200", light: "bg-pink-50" },
  lashes: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", border: "border-amber-200", light: "bg-amber-50" },
  lash: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500", border: "border-amber-200", light: "bg-amber-50" },
  culinary: { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500", border: "border-green-200", light: "bg-green-50" },
  style: { bg: "bg-teal-100", text: "text-teal-700", dot: "bg-teal-500", border: "border-teal-200", light: "bg-teal-50" },
  event: { bg: "bg-indigo-100", text: "text-indigo-700", dot: "bg-indigo-500", border: "border-indigo-200", light: "bg-indigo-50" },
  custom: { bg: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-500", border: "border-gray-200", light: "bg-gray-50" }
};

const CATEGORY_ICONS = {
  makeup: Sparkles, hair: Scissors, nails: Palette, nail: Palette,
  lashes: Eye, lash: Eye, culinary: Utensils, event: CalendarIcon, custom: Wand2
};

const IconComponents = {
  Scissors, Sparkles, Palette, Eye, Utensils, PartyPopper, Heart, Cake,
  Star, GraduationCap, ShoppingBag, TrendingUp, Users, Building, Presentation, CalendarIcon
};

const getIconComponent = (iconName) => {
  const componentName = ICON_MAP[iconName] || ICON_MAP.default;
  return IconComponents[componentName] || Sparkles;
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * GoogleCalendarMonth - Month view with drag & drop support
 */
export const GoogleCalendarMonth = ({ 
  selectedDate, 
  onDateSelect, 
  experiences = [],
  onViewExperience,
  onCreateExperience,
  onReschedule
}) => {
  const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());
  const [draggedExp, setDraggedExp] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const { visibleCategories, categoryMap } = useExperienceCategories();

  // Only show categories that the user actually has experiences in
  const userCategories = useMemo(() => {
    const ids = new Set();
    (experiences || []).forEach(e => {
      const c = (e.category || "").toLowerCase();
      if (c) ids.add(c);
    });
    return visibleCategories.filter(cat => ids.has((cat.id || "").toLowerCase()));
  }, [experiences, visibleCategories]);

  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const days = [];
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startPadding - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false, isToday: false });
    }
    const today = new Date();
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true, isToday: date.toDateString() === today.toDateString() });
    }
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false, isToday: false });
    }
    return days;
  }, [currentMonth]);

  const experiencesByDate = useMemo(() => {
    const map = {};
    experiences.forEach(exp => {
      // Iter 9.50+ R9 - primary slot at full opacity + secondaries reduced.
      const hardLocked = exp.plan_status === "provider_confirmed" || exp.primary_slot_locked === true;
      if (exp.date || exp.target_date) {
        const dateStr = new Date(exp.date || exp.target_date).toDateString();
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push({ ...exp, _slot_role: "primary" });
      }
      if (!hardLocked && Array.isArray(exp.secondary_slots)) {
        exp.secondary_slots.forEach((slot, idx) => {
          if (!slot.date) return;
          const dateStr = new Date(slot.date).toDateString();
          if (!map[dateStr]) map[dateStr] = [];
          map[dateStr].push({ ...exp, _slot_role: "secondary", _slot_idx: idx, _slot_time: slot.time, experience_id: `${exp.experience_id || ""}__alt${idx}` });
        });
      }
    });
    return map;
  }, [experiences]);
  // hoisted shim - this replaces the legacy simple builder above with the
  // Iter 9.50+ R9 multi-slot view (primary + secondary fades).


  const handleDragStart = useCallback((e, exp) => {
    setDraggedExp(exp);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', exp.experience_id);
  }, []);

  const handleDragOver = useCallback((e, dateStr) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(dateStr);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback((e, date) => {
    e.preventDefault();
    setDropTarget(null);
    if (draggedExp) {
      onReschedule?.(draggedExp.experience_id, date.toISOString());
      setDraggedExp(null);
    }
  }, [draggedExp, onReschedule]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="google-calendar-month">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setCurrentMonth(new Date()); onDateSelect?.(new Date()); }}>Today</Button>
          <div className="flex items-center">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <h2 className="text-lg font-semibold text-gray-900">
            {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h2>
        </div>
        <div className="hidden md:flex items-center gap-3 text-xs text-gray-500 flex-wrap">
          {userCategories.map(cat => (
            <span key={cat.id} className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }}></span>
              {cat.name}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-gray-200">
        {WEEKDAYS.map(day => (
          <div key={day} className="py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">{day}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {calendarDays.map((day, idx) => {
          const dayExperiences = experiencesByDate[day.date.toDateString()] || [];
          const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();
          const isDropping = dropTarget === day.date.toDateString();
          
          return (
            <div
              key={idx}
              className={`
                min-h-[120px] border-b border-r border-gray-100 p-1 cursor-pointer transition-colors
                ${!day.isCurrentMonth ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}
                ${isSelected ? 'ring-2 ring-inset ring-purple-500' : ''}
                ${day.isToday ? 'bg-purple-50' : ''}
                ${isDropping ? 'bg-purple-100 ring-2 ring-inset ring-purple-400' : ''}
              `}
              onClick={() => onDateSelect?.(day.date)}
              onDragOver={(e) => handleDragOver(e, day.date.toDateString())}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, day.date)}
              data-testid={`calendar-day-${day.date.getDate()}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                  ${day.isToday ? 'bg-purple-600 text-white' : ''} ${!day.isCurrentMonth ? 'text-gray-400' : 'text-gray-700'}
                `}>{day.date.getDate()}</span>
              </div>
              <div className="space-y-1 overflow-hidden">
                {dayExperiences.slice(0, 3).map((exp, i) => {
                  const catInfo = categoryMap[exp.category] || categoryMap[exp.category?.toLowerCase()] || {};
                  const bgColor = catInfo.color || "#6B7280";
                  // Iter 9.50+ R9 - title shown to USER (not host view): use
                  // the experience/occasion title that the user has named.
                  // Never the user's own name.
                  const userTitle = exp.title || exp.event_name || exp.experience_name || exp.name || (exp.category || "experience");
                  const isSecondary = exp._slot_role === "secondary";
                  return (
                    <div
                      key={exp.experience_id || i}
                      draggable={!isSecondary}
                      onDragStart={(e) => handleDragStart(e, exp)}
                      className={`text-white text-xs px-2 py-1 rounded truncate flex items-center gap-1 cursor-grab hover:opacity-90 active:cursor-grabbing ${
                        isSecondary ? "border border-dashed border-white/60 opacity-40" : ""
                      }`}
                      style={{
                        backgroundColor: bgColor,
                        ...(isSecondary ? { backgroundImage: "linear-gradient(135deg, transparent 0 4px, rgba(255,255,255,0.18) 4px 8px)" } : {}),
                      }}
                      onClick={(e) => { e.stopPropagation(); onViewExperience?.(exp); }}
                      data-testid={`event-${exp.experience_id}`}
                    >
                      {isSecondary && <span className="text-[8px] uppercase tracking-wider mr-0.5">alt</span>}
                      <span className="truncate">{userTitle}</span>
                    </div>
                  );
                })}
                {dayExperiences.length > 3 && (
                  <div className="text-xs text-gray-500 px-1">+{dayExperiences.length - 3} more</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * CalendarWeekView - Week view with drag & drop
 */
export const CalendarWeekView = ({ 
  selectedDate, 
  onDateSelect, 
  experiences = [],
  onViewExperience,
  onReschedule
}) => {
  const [weekStart, setWeekStart] = useState(() => {
    const date = selectedDate || new Date();
    const start = new Date(date);
    start.setDate(date.getDate() - date.getDay());
    return start;
  });
  const [draggedExp, setDraggedExp] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const { categoryMap } = useExperienceCategories();

  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      days.push(date);
    }
    return days;
  }, [weekStart]);

  const experiencesByDate = useMemo(() => {
    const map = {};
    experiences.forEach(exp => {
      // Iter 9.50+ R9 - primary slot at full opacity + secondaries at reduced.
      const hardLocked = exp.plan_status === "provider_confirmed" || exp.primary_slot_locked === true;
      if (exp.date || exp.target_date) {
        const dateStr = new Date(exp.date || exp.target_date).toDateString();
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push({ ...exp, _slot_role: "primary" });
      }
      if (!hardLocked && Array.isArray(exp.secondary_slots)) {
        exp.secondary_slots.forEach((slot, idx) => {
          if (!slot.date) return;
          const dateStr = new Date(slot.date).toDateString();
          if (!map[dateStr]) map[dateStr] = [];
          map[dateStr].push({ ...exp, _slot_role: "secondary", _slot_idx: idx, _slot_time: slot.time, experience_id: `${exp.experience_id || ""}__alt${idx}` });
        });
      }
    });
    return map;
  }, [experiences]);

  const handleDragStart = useCallback((e, exp) => {
    setDraggedExp(exp);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDrop = useCallback((e, date) => {
    e.preventDefault();
    setDropTarget(null);
    if (draggedExp) {
      onReschedule?.(draggedExp.experience_id, date.toISOString());
      setDraggedExp(null);
    }
  }, [draggedExp, onReschedule]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="calendar-week-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
            const ns = new Date(weekStart); ns.setDate(weekStart.getDate() - 7); setWeekStart(ns);
          }}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => {
            const ns = new Date(weekStart); ns.setDate(weekStart.getDate() + 7); setWeekStart(ns);
          }}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold text-gray-900">
            {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {
              new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            }
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-7 divide-x divide-gray-100">
        {weekDays.map((date, idx) => {
          const dayExperiences = experiencesByDate[date.toDateString()] || [];
          const isToday = date.toDateString() === new Date().toDateString();
          const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
          const isDropping = dropTarget === date.toDateString();
          
          return (
            <div
              key={idx}
              className={`min-h-[300px] p-2 cursor-pointer transition-colors
                ${isToday ? 'bg-purple-50' : 'bg-white'}
                ${isSelected ? 'ring-2 ring-inset ring-purple-500' : ''}
                ${isDropping ? 'bg-purple-100' : ''}
              `}
              onClick={() => onDateSelect?.(date)}
              onDragOver={(e) => { e.preventDefault(); setDropTarget(date.toDateString()); }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) => handleDrop(e, date)}
            >
              <div className="text-center mb-3">
                <div className="text-xs text-gray-500 uppercase">{WEEKDAYS[idx]}</div>
                <div className={`text-xl font-semibold w-10 h-10 mx-auto flex items-center justify-center rounded-full
                  ${isToday ? 'bg-purple-600 text-white' : 'text-gray-900'}
                `}>{date.getDate()}</div>
              </div>

              <div className="space-y-2">
                {dayExperiences.map((exp, i) => {
                  const catInfo = categoryMap[exp.category] || categoryMap[exp.category?.toLowerCase()] || {};
                  const bgColor = catInfo.color || "#6B7280";
                  const coverImg = exp.cover_image || exp.event_cover_image || (exp.inspo?.[0]?.image_url);
                  
                  return (
                    <div
                      key={exp.experience_id || i}
                      draggable
                      onDragStart={(e) => handleDragStart(e, exp)}
                      className="rounded-lg overflow-hidden border border-gray-200 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                      onClick={(e) => { e.stopPropagation(); onViewExperience?.(exp); }}
                      data-testid={`week-event-${exp.experience_id}`}
                    >
                      {/* Mini cover image */}
                      {coverImg && (
                        <div className="h-12 overflow-hidden">
                          <img src={coverImg} alt="" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="p-1.5" style={{ borderLeft: `3px solid ${bgColor}` }}>
                        <div className="flex items-center gap-1 mb-0.5">
                          <GripVertical className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="font-medium text-xs truncate">{exp.name}</span>
                        </div>
                        {exp.provider_name && (
                          <div className="text-[10px] text-gray-500 truncate pl-4">{exp.provider_name}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * CoverImagePicker - Modal to pick a cover image from user's inspo
 */
const CoverImagePicker = ({ isOpen, onClose, experience, onSetCover }) => {
  const [inspoItems, setInspoItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState(null);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  useEffect(() => {
    if (!isOpen || !experience) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;
    
    setLoading(true);
    
    // Fetch inspo for the experience's category AND linked categories
    const categories = [experience.category];
    if (experience.linked_experiences) {
      experience.linked_experiences.forEach(le => {
        if (le.category && !categories.includes(le.category)) categories.push(le.category);
      });
    }
    
    Promise.all(
      categories.filter(c => c !== 'event' && c !== 'custom').map(cat =>
        fetch(`${API}/inspo?category=${cat}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : { inspo: [] })
          .then(d => (d.inspo || []).map(i => ({ ...i, fromCategory: cat })))
          .catch(() => [])
      )
    ).then(results => {
      const all = results.flat().filter(i => i.image_url || i.url);
      setInspoItems(all);
      setLoading(false);
    });
  }, [isOpen, experience, API]);
  
  const handleSave = () => {
    if (!selectedUrl) return;
    onSetCover(experience.experience_id, selectedUrl);
    onClose();
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Set Cover Image
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3">
          {/* Current cover preview */}
          {selectedUrl && (
            <div className="h-32 rounded-xl overflow-hidden border-2 border-purple-400">
              <img src={selectedUrl} alt="Selected cover" className="w-full h-full object-cover" />
            </div>
          )}
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              <span className="ml-2 text-sm text-gray-500">Loading your inspo...</span>
            </div>
          ) : inspoItems.length > 0 ? (
            <>
              <p className="text-sm text-gray-600">Pick from your saved inspiration:</p>
              <div className="grid grid-cols-3 gap-2">
                {inspoItems.map((inspo, idx) => {
                  const url = inspo.image_url || inspo.url;
                  const isSelected = url === selectedUrl;
                  return (
                    <button
                      key={inspo.inspo_id || idx}
                      onClick={() => setSelectedUrl(url)}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        isSelected ? 'border-purple-500 ring-2 ring-purple-300' : 'border-gray-200 hover:border-gray-400'
                      }`}
                      data-testid={`cover-pick-${idx}`}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {isSelected && (
                        <div className="absolute inset-0 bg-purple-500/30 flex items-center justify-center">
                          <Check className="w-6 h-6 text-white" />
                        </div>
                      )}
                      <Badge className="absolute bottom-1 left-1 text-[8px] bg-white/90 text-gray-600">
                        {inspo.fromCategory}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <Camera className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No saved inspo for this category yet.</p>
              <p className="text-xs text-gray-400 mt-1">Save some inspiration first from the Inspo tab.</p>
            </div>
          )}
          
          <Button 
            onClick={handleSave} 
            disabled={!selectedUrl}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
            data-testid="save-cover-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            Set as Cover
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * CalendarListView - Grid cards with cover images from inspo
 */
export const CalendarListView = ({ 
  experiences = [],
  onViewExperience,
  onEditExperience,
  onShareExperience,
  onDeleteExperience,
  onRefresh,
  userInspo = [],
  selectMode = false,
  selectedIds = new Set(),
  onToggleSelect,
}) => {
  const { categoryMap } = useExperienceCategories();
  const [coverPickerExp, setCoverPickerExp] = useState(null);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  // Group user inspo by category for event cards
  const inspoByCategory = useMemo(() => {
    const map = {};
    userInspo.forEach(item => {
      const cat = item.category || 'custom';
      if (!map[cat]) map[cat] = [];
      if (item.image_url || item.url) map[cat].push(item);
    });
    return map;
  }, [userInspo]);
  
  const handleSetCover = async (experienceId, imageUrl) => {
    const token = localStorage.getItem("session_token");
    if (!token) return;
    try {
      const res = await fetch(`${API}/user/experiences/${experienceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cover_image: imageUrl })
      });
      if (res.ok) {
        toast.success("Cover image updated!");
        onRefresh?.();
      } else {
        toast.error("Failed to update cover");
      }
    } catch {
      toast.error("Failed to update cover");
    }
  };
  
  if (experiences.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-testid="calendar-list-empty">
        <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="font-medium text-gray-700 mb-1">No experiences yet</h3>
        <p className="text-sm text-gray-500">Start planning your first experience</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="calendar-list-view">
      {experiences.map((exp) => {
        const colors = CATEGORY_COLORS[exp.category] || CATEGORY_COLORS.custom;
        const Icon = CATEGORY_ICONS[exp.category] || CalendarIcon;
        const catInfo = categoryMap[exp.category] || {};
        const hasInspo = exp.inspo?.length > 0 || exp.inspiration_images?.length > 0;
        
        // Cover image priority: explicit cover > event cover > first inspo > linked experience inspo
        let coverImage = exp.cover_image || exp.event_cover_image;
        if (!coverImage && hasInspo) {
          coverImage = exp.inspo?.[0]?.image_url || exp.inspiration_images?.[0]?.image_url;
        }
        if (!coverImage && exp.linked_experiences?.length > 0) {
          // Try to find a cover from linked experiences
          for (const linked of exp.linked_experiences) {
            if (linked.cover_image) { coverImage = linked.cover_image; break; }
            if (linked.inspo?.[0]?.image_url) { coverImage = linked.inspo[0].image_url; break; }
          }
        }
        
        const orgLogo = exp.org_logo || exp.event_org_logo;
        
        return (
          <div
            key={exp.experience_id}
            className={`bg-white rounded-xl border overflow-hidden hover:shadow-lg transition-shadow group relative ${selectMode && selectedIds.has(exp.experience_id) ? 'border-black ring-2 ring-black/10' : 'border-gray-200'}`}
            data-testid={`list-card-${exp.experience_id}`}
            onClick={selectMode ? () => onToggleSelect?.(exp.experience_id) : undefined}
          >
            {/* Select checkbox overlay */}
            {selectMode && (
              <div className="absolute top-2 left-2 z-10">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${selectedIds.has(exp.experience_id) ? 'bg-black border-black' : 'bg-white/90 border-gray-300'}`}>
                  {selectedIds.has(exp.experience_id) && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
              </div>
            )}
            {/* Cover Image or Gradient */}
            <div className={`h-36 relative ${!coverImage ? `bg-gradient-to-br ${colors.light}` : ''}`}>
              {coverImage ? (
                <img src={coverImage} alt="" className="w-full h-full object-cover" />
              ) : (
                <button 
                  onClick={(e) => { e.stopPropagation(); setCoverPickerExp(exp); }}
                  className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors group/cover" 
                  style={{ background: catInfo.color ? `linear-gradient(135deg, ${catInfo.color}15, ${catInfo.color}30)` : undefined }}
                  data-testid={`set-cover-${exp.experience_id}`}
                >
                  <Icon className={`w-8 h-8 ${colors.text} opacity-30 group-hover/cover:opacity-50 transition-opacity`} />
                  <span className="text-xs text-gray-400 mt-2 group-hover/cover:text-gray-600 transition-colors flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" /> Set cover image
                  </span>
                </button>
              )}
              {orgLogo && (
                <div className="absolute bottom-2 left-2 w-8 h-8 rounded-lg bg-white/90 shadow-sm overflow-hidden flex items-center justify-center">
                  <img src={orgLogo} alt="" className="w-6 h-6 object-contain" />
                </div>
              )}
              <button
                onClick={() => onViewExperience?.(exp)}
                className="absolute top-2 right-2 p-2 bg-white/90 rounded-full shadow-sm hover:bg-white transition-colors"
                data-testid={`view-btn-${exp.experience_id}`}
              >
                <Eye className="w-4 h-4 text-gray-700" />
              </button>
              <Badge 
                className="absolute top-2 left-2 text-white text-xs"
                style={{ backgroundColor: catInfo.color || undefined }}
              >
                {catInfo.name || exp.category}
              </Badge>
            </div>

            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-1 leading-snug line-clamp-2">{exp.name}</h3>
              <p className="text-sm text-gray-500 mb-2">
                {(exp.date || exp.target_date)
                  ? new Date(exp.date || exp.target_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                  : 'Date TBD'
                }
              </p>
              {exp.provider_name && (
                <p className="text-xs text-gray-400 truncate">with {exp.provider_name}</p>
              )}
              {hasInspo && (
                <div className="flex gap-1 mt-2">
                  {(exp.inspo || exp.inspiration_images || []).slice(0, 4).map((img, i) => (
                    <div key={i} className="w-8 h-8 rounded-md overflow-hidden border border-gray-100">
                      <img src={img.image_url || img} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                  {(exp.inspo || exp.inspiration_images || []).length > 4 && (
                    <div className="w-8 h-8 rounded-md bg-gray-100 flex items-center justify-center text-[10px] text-gray-500">
                      +{(exp.inspo || exp.inspiration_images || []).length - 4}
                    </div>
                  )}
                </div>
              )}
              {exp.linked_experiences?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  {/* Collage: gather all inspo from all linked categories */}
                  {(() => {
                    const allCollageItems = [];
                    exp.linked_experiences.forEach(linked => {
                      const linkedCat = categoryMap[linked.category] || {};
                      const catInspoItems = inspoByCategory[linked.category] || [];
                      catInspoItems.slice(0, 3).forEach(img => {
                        allCollageItems.push({ url: img.image_url || img.url, color: linkedCat.color, category: linkedCat.name || linked.category });
                      });
                    });
                    
                    if (allCollageItems.length === 0) {
                      // No inspo - just show linked badges
                      return (
                        <div className="flex flex-wrap gap-1">
                          {exp.linked_experiences.map((linked, li) => {
                            const lCat = categoryMap[linked.category] || {};
                            const LIcon = CATEGORY_ICONS[linked.category] || CalendarIcon;
                            return (
                              <span key={li} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                                style={{ backgroundColor: lCat.color ? `${lCat.color}15` : undefined, color: lCat.color || undefined }}>
                                <LIcon className="w-2.5 h-2.5" /> {lCat.name || linked.category}
                              </span>
                            );
                          })}
                        </div>
                      );
                    }
                    
                    // Mosaic collage layout
                    const count = allCollageItems.length;
                    return (
                      <div className={`grid gap-1 h-16 ${count === 1 ? 'grid-cols-1' : count === 2 ? 'grid-cols-2' : count <= 4 ? 'grid-cols-2 grid-rows-2' : 'grid-cols-3 grid-rows-2'}`}>
                        {allCollageItems.slice(0, 6).map((item, i) => (
                          <div key={i} className={`relative rounded overflow-hidden ${count <= 4 && i === 0 ? (count === 3 ? 'row-span-2' : '') : ''}`}>
                            <img src={item.url} alt="" className="w-full h-full object-cover" />
                            <div className="absolute bottom-0 left-0 w-1.5 h-full" style={{ backgroundColor: item.color || '#6B7280' }} />
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {/* Category labels below collage */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {exp.linked_experiences.map((linked, li) => {
                      const lCat = categoryMap[linked.category] || {};
                      const LIcon = CATEGORY_ICONS[linked.category] || CalendarIcon;
                      return (
                        <span key={li} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                          style={{ backgroundColor: lCat.color ? `${lCat.color}15` : undefined, color: lCat.color || undefined }}>
                          <LIcon className="w-2.5 h-2.5" /> {lCat.name || linked.category}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {/* Cover Image Picker Modal */}
      <CoverImagePicker
        isOpen={!!coverPickerExp}
        onClose={() => setCoverPickerExp(null)}
        experience={coverPickerExp}
        onSetCover={handleSetCover}
      />
    </div>
  );
};

export default { GoogleCalendarMonth, CalendarWeekView, CalendarListView };
